import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../Common/Loading';

// --- Interfaces ---
interface DocumentRow {
    session_id: string;
    school_code: string;
    family_code: string;
    fname: string;
    view_url: string;
    row_index: number;
}

interface DicGroup {
    dic_code: string;
    total: number;
    documents: DocumentRow[];
}

const EL_QuestionInsights: React.FC = () => {
    // --- Config ---
    const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;
    const [searchParams] = useSearchParams();

    // --- State ---
    const [data, setData] = useState<DicGroup[]>([]);
    const [loading, setLoading] = useState(true);

    // --- URL Params ---
    const dicCode = searchParams.get('DICCode') || '';
    const lessonCode = searchParams.get('Title') || ''; // 'Title' is used as LessonCode
    const qnNo = searchParams.get('Qnno') || '';
    const fromDate = searchParams.get('Fromlsndate') || '';
    const toDate = searchParams.get('Tolsndate') || '';
    const testCode = searchParams.get('TestCode') || '';

    // --- Styles (Matching VB exactly) ---
    const styles = {
        body: { fontFamily: 'Arial, sans-serif', fontSize: '14px', margin: 0, padding: 0, backgroundColor: '#fff', minHeight: '100vh' },
        header: { backgroundColor: 'teal', color: 'white', textAlign: 'center' as const, fontSize: '16pt', padding: '6px' },
        subHeaderArea: { padding: '10px 15px', backgroundColor: '#fff', display: 'flex', gap: '20px', fontSize: '14px' },
        tableContainer: { padding: '0 15px' },
        table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '14px', background: '#ffffff' },
        th: { background: '#f2f2f2', color: '#333', padding: '8px', textAlign: 'center' as const, border: '1px solid #ccc', fontWeight: 600 },
        td: { padding: '8px', textAlign: 'center' as const, border: '1px solid #ccc' },
        dicHeaderTd: { background: '#f2f2f2', fontWeight: 'bold', padding: '8px', textAlign: 'center' as const, border: '1px solid #ccc' },
        link: { color: '#0073e6', textDecoration: 'underline', cursor: 'pointer' },
        emptyRow: { textAlign: 'center' as const, color: '#777', padding: '15px', border: '1px solid #ccc' }
    };

    // --- Fetch Data ---
    useEffect(() => {
        const fetchInsights = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}api/get_question_insights`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        dic_code: dicCode,
                        lesson_code: lessonCode,
                        test_code: testCode,   // ADD THIS LINE
                        qn_no: qnNo,
                        from_date: fromDate,
                        to_date: toDate
                    })
                });

                const json = await res.json();

                if (json.error) {
                    alert(json.error);
                } else {
                    setData(json.results || []);

                    // Crucial: Store ALL URLs in sessionStorage so the popup can use Next/Prev
                    if (json.all_urls) {
                        sessionStorage.setItem("questionUrls", JSON.stringify(json.all_urls));
                    }
                }
            } catch (err) {
                console.error("Fetch Error:", err);
            } finally {
                setLoading(false);
            }
        };

        if (lessonCode && qnNo) {
            fetchInsights();
        } else {
            setLoading(false);
        }
    }, [dicCode, lessonCode, qnNo, fromDate, toDate]);

    // --- Handlers ---
    const handleViewClick = (e: React.MouseEvent, url: string, rowIndex: number) => {
        e.preventDefault();

        // Update current index for the popup's internal Next/Prev logic
        sessionStorage.setItem("currentIndex", rowIndex.toString());

        // FIX: Ensure URL is absolute (Starts from root) to prevent /SUPPORT/LessonBrowser issue
        let finalUrl = url;
        if (!finalUrl.startsWith("/")) {
            finalUrl = "/" + finalUrl;
        }

        // Replicate VB window.open logic exactly
        const w = 1200;
        const h = 800;
        const left = (window.screen.width / 2) - (w / 2);
        const top = (window.screen.height / 2) - (h / 2);

        window.open(
            finalUrl,
            "_blank",
            `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
    };

    return (
        <div style={styles.body}>
            {loading && <Loading />}

            {/* 1. Header */}
            <div style={styles.header}>
                Family-wise Question Insights
            </div>

            {/* 2. Sub-Header Information */}
            <div style={styles.subHeaderArea}>
                <span>Lessoncode: &nbsp;<b>{lessonCode}</b></span>
                <span>Testcode: &nbsp;<b>{testCode}</b></span> {/* Legacy leaves this empty */}
                <span>Qnno: &nbsp;<b>{qnNo}</b></span>
            </div>

            {/* 3. Data Table */}
            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.th, width: '70px' }}>Times Done</th>
                            <th style={styles.th}>SchoolCode</th>
                            <th style={styles.th}>FamilyCode</th>
                            <th style={styles.th}>Fname</th>
                            <th style={{ ...styles.th, width: '80px' }}>User Answers</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 && !loading && (
                            <tr>
                                <td colSpan={5} style={styles.emptyRow}>No data available</td>
                            </tr>
                        )}

                        {data.map((group, gIdx) => (
                            <React.Fragment key={`group-${gIdx}`}>
                                {/* DIC Group Header Row */}
                                <tr>
                                    <td colSpan={5} style={styles.dicHeaderTd}>
                                        DIC Code : {group.dic_code} (Total: {group.total})
                                    </td>
                                </tr>

                                {/* Student Data Rows */}
                                {group.documents.map((doc, dIdx) => (
                                    <tr key={`doc-${dIdx}`} style={{ backgroundColor: dIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                        <td style={styles.td}>{doc.session_id || '1'}</td>
                                        <td style={styles.td}>{doc.school_code}</td>
                                        <td style={styles.td}>{doc.family_code}</td>
                                        <td style={styles.td}>{doc.fname}</td>
                                        <td style={styles.td}>
                                            <a
                                                style={styles.link}
                                                onClick={(e) => handleViewClick(e, doc.view_url, doc.row_index)}
                                            >
                                                View
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EL_QuestionInsights;